function normalizeSetupName(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isNormalizedNameTooShort(value, minLength = 3) {
  return normalizeSetupName(value).length < minLength;
}

function hasNormalizedNameDuplicate(names, candidateName) {
  const normalizedCandidate = normalizeSetupName(candidateName).toUpperCase();

  if (normalizedCandidate === "") {
    return false;
  }

  return names.some(
    (name) => normalizeSetupName(name).toUpperCase() === normalizedCandidate
  );
}

function buildDefaultTeamNames(t) {
  return [
    { name: t("cpInsert.team1") },
    { name: t("cpInsert.team2") },
    { name: t("cpInsert.team3") },
    { name: t("cpInsert.team4") },
  ];
}

function normalizeLegacyDefaultTeamName(value, index, fallbackTeamNames) {
  const normalizedName = normalizeSetupName(value);
  const normalizedNameUpper = normalizedName.toUpperCase();
  const legacyNamesByIndex = [
    ["TEAM 1", "TEAM 1D", "T 1"],
    ["TEAM 2", "TEAM 2D", "T 2"],
    ["TEAM 3", "TEAM 3D", "T 3"],
    ["TEAM 4", "TEAM 4D", "T 4"],
  ];

  if (legacyNamesByIndex[index]?.includes(normalizedNameUpper)) {
    return fallbackTeamNames[index].name;
  }

  return normalizedName;
}

function ensureFourTeamNames(teamNames, t) {
  const fallbackTeamNames = buildDefaultTeamNames(t);
  const normalizedTeamNames = Array.isArray(teamNames) ? [...teamNames] : [];

  while (normalizedTeamNames.length < 4) {
    normalizedTeamNames.push({
      name: fallbackTeamNames[normalizedTeamNames.length].name,
    });
  }

  return normalizedTeamNames.slice(0, 4).map((team, index) => ({
    ...team,
    name:
      normalizeLegacyDefaultTeamName(team?.name, index, fallbackTeamNames) ||
      fallbackTeamNames[index].name,
  }));
}

function validateTeamNames(teamNames, maxTeams) {
  const scopedTeamNames = teamNames.slice(0, maxTeams).map((team) => ({
    ...team,
    name: normalizeSetupName(team?.name),
  }));
  const inputErrors = teamNames.map(() => ({ inputError: false }));
  let hasAnyErrors = false;

  scopedTeamNames.forEach((team, index) => {
    if (team.name.length < 3) {
      inputErrors[index].inputError = true;
      hasAnyErrors = true;
      return;
    }

    const duplicateCount = scopedTeamNames.filter(
      (candidate) => candidate.name.toUpperCase() === team.name.toUpperCase()
    ).length;

    if (duplicateCount > 1) {
      inputErrors[index].inputError = true;
      hasAnyErrors = true;
    }
  });

  return {
    normalizedTeamNames: teamNames.map((team, index) => ({
      ...team,
      name: scopedTeamNames[index]?.name ?? normalizeSetupName(team?.name),
    })),
    inputErrors,
    teamNamesReady: !hasAnyErrors,
  };
}

function buildPlayerSelectionReadiness({
  isTeamGame,
  maxIndividualPlayers = 0,
  maxTeamPlayers = 0,
  maxTeams = 0,
  minIndividualPlayers = 0,
  minTeamPlayers = 0,
  minTeams = 0,
  selectedPlayers = 0,
  teamSelectionCounts = [],
}) {
  if (!isTeamGame) {
    return (
      selectedPlayers >= minIndividualPlayers &&
      selectedPlayers <= maxIndividualPlayers
    );
  }

  const activeTeamCounts = teamSelectionCounts.filter((count) => count !== 0);
  const hasMismatchedTeamSizes =
    activeTeamCounts.length > 1 &&
    activeTeamCounts.some((count) => count !== activeTeamCounts[0]);

  if (hasMismatchedTeamSizes) {
    return false;
  }

  let teamsWithCorrectPlayers = 0;
  let hasIncorrectTeamCount = false;

  teamSelectionCounts.slice(0, maxTeams).forEach((count) => {
    if (count >= minTeamPlayers && count <= maxTeamPlayers) {
      teamsWithCorrectPlayers++;
      return;
    }

    if (count !== 0) {
      hasIncorrectTeamCount = true;
    }
  });

  if (hasIncorrectTeamCount) {
    return false;
  }

  return teamsWithCorrectPlayers >= minTeams && teamsWithCorrectPlayers <= maxTeams;
}

function buildReadyToCreateScoreCard({
  cardClubID,
  playersSelectedReady,
  teamNamesReady,
}) {
  return Boolean(cardClubID) && playersSelectedReady && teamNamesReady;
}

export {
  buildDefaultTeamNames,
  buildPlayerSelectionReadiness,
  buildReadyToCreateScoreCard,
  ensureFourTeamNames,
  hasNormalizedNameDuplicate,
  isNormalizedNameTooShort,
  normalizeSetupName,
  validateTeamNames,
};
